import type { SequenceCreateMode } from "@components/sequence-create/sequenceCreateStore";
import { settingsPages } from "@lib/settings";
import type { LucideIcon } from "lucide-react";
import { FlaskConicalIcon, PencilIcon, ScissorsIcon } from "lucide-react";

type CommandHelpStatus = "has-help" | "no-help";

type NavigateAction = {
  type: "navigate";
  to: string;
};

type CreateSequenceAction = {
  type: "create-sequence";
  mode: SequenceCreateMode;
};

export type CommandAction = NavigateAction | CreateSequenceAction;

export type CommandGroup = "Create" | "Library" | "Reference" | "Settings";

export type PaletteCommandEntry = {
  id: string;
  label: string;
  group: CommandGroup;
  keywords: string[];
  icon: LucideIcon;
  action: CommandAction;
  helpStatus: CommandHelpStatus;
  shortcut?: string;
};

export const groupOrder: readonly CommandGroup[] = [
  "Create",
  "Library",
  "Reference",
  "Settings",
];

const settingsIdByPath: Record<(typeof settingsPages)[number]["path"], string> =
  {
    "/settings/general": "settings-general",
    "/settings/account": "settings-account",
    "/settings/services": "settings-services",
    "/settings/storage": "settings-storage",
    "/settings/viewers": "settings-viewers",
    "/settings/help": "settings-help",
  };

const settingsCommands: PaletteCommandEntry[] = settingsPages.map((page) => ({
  id: settingsIdByPath[page.path],
  label: `Open ${page.label} Settings`,
  group: "Settings" as const,
  keywords: ["settings", "preferences", ...page.keywords],
  icon: page.icon,
  action: { type: "navigate" as const, to: page.path },
  helpStatus: "no-help" as const,
}));

export const commands: readonly PaletteCommandEntry[] = [
  {
    id: "create-sequence-simple",
    label: "Create Sequence: Simple",
    group: "Create",
    keywords: ["create", "new", "simple", "sequence"],
    icon: PencilIcon,
    action: { type: "create-sequence", mode: "simple" },
    helpStatus: "no-help",
  },
  {
    id: "create-sequence-fasta",
    label: "Create Sequence: FASTA",
    group: "Create",
    keywords: ["create", "new", "fasta", "import"],
    icon: PencilIcon,
    action: { type: "create-sequence", mode: "fasta" },
    helpStatus: "no-help",
  },
  {
    id: "create-sequence-accession",
    label: "Create Sequence: Accession",
    group: "Create",
    keywords: ["create", "new", "accession"],
    icon: PencilIcon,
    action: { type: "create-sequence", mode: "accession" },
    helpStatus: "no-help",
  },
  {
    id: "primers-library",
    label: "Browse Primers",
    group: "Library",
    keywords: ["primer", "primers", "library"],
    icon: FlaskConicalIcon,
    action: { type: "navigate", to: "/library/primers" },
    helpStatus: "no-help",
  },
  {
    id: "restriction-enzymes",
    label: "Browse Restriction Enzymes",
    group: "Reference",
    keywords: ["re", "enzyme", "restriction"],
    icon: ScissorsIcon,
    action: { type: "navigate", to: "/reference/enzymes" },
    helpStatus: "no-help",
  },
  ...settingsCommands,
];
